import { useMemo } from 'react';
import { fromZonedTime } from 'date-fns-tz';
import { isDateAvailable } from '@/lib/helpers/helpers';
import { DATE_PRESET_LIMITS } from '@/state/legs/legsTypes';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { getFilterQueryParams } from '@/components/FilterPopup/filterHelpers';
import useSymbolsListQuery from '@/hooks/useSymbolsListQuery';
import { selectMemberTimezone } from '@/state/member/memberSelectors';
import {
    selectCurrentAdvancedSelection,
    selectDateFilter,
    selectSymbolsFilter,
} from '@/state/clientStorage/clientStorageSelectors';
import { useAccountNames } from '@/hooks/useAccountsQuery';

export const definedFetchLegsParams = (
    accountIds: number[] | string[],
    paramsData,
    initial: boolean = false,
) => {
    const { dateSelection, symbolsList, symbolFilter, legsFilters, accounts, userTimezone } =
        paramsData;
    const { label = '' } = dateSelection;

    let params: any = {
        offset: 0,
        startDate: fromZonedTime(dateSelection.startDate, userTimezone || ''),
        endDate: fromZonedTime(dateSelection.endDate, userTimezone || ''),
    };

    if (!isDateAvailable(label)) {
        params = {
            offset: 0,
            limit: DATE_PRESET_LIMITS[dateSelection.label],
        };
    }
    if (legsFilters) {
        params = { ...params, ...getFilterQueryParams(legsFilters) };
    }

    if (initial) {
        params.symbols = [];
    }
    if (symbolFilter.length > 0 && symbolFilter.length < symbolsList.length) {
        params.symbols = symbolFilter;
    }
    if (Array.isArray(accountIds) && Object.keys(accounts).length !== accountIds.length) {
        params.accountIds = accountIds;
    }
    return params;
};

interface UseGetFetchLegsParamsProps {
    isMainPage: boolean;
    accountIds: number[];
    withoutLegFilter?: boolean;
    initial?: boolean;
}

const useGetFetchLegsParams = ({
    isMainPage = false,
    accountIds,
    initial = false,
    withoutLegFilter = false,
}: UseGetFetchLegsParamsProps): any => {
    const { symbolsList } = useSymbolsListQuery({ accountIds });

    const legsFilters = useSelector((state: RootState) => selectCurrentAdvancedSelection(state));
    const { data: accounts = {} } = useAccountNames();
    const userTimezone = useSelector((state: RootState) => selectMemberTimezone(state));
    const symbolFilter = useSelector((state: RootState) =>
        selectSymbolsFilter(state, { accountId: accountIds[0], isMainPage }),
    );
    const dateSelection = useSelector((state: RootState) =>
        selectDateFilter(state, { accountId: accountIds[0], isMainPage }),
    );

    const getParams = (accountIds, initial) => {
        return definedFetchLegsParams(
            accountIds,
            {
                dateSelection,
                symbolsList,
                symbolFilter,
                legsFilters: withoutLegFilter ? {} : legsFilters,
                accounts,
                userTimezone,
            },
            initial,
        );
    };

    const params = useMemo(() => {
        return getParams(accountIds, initial);
    }, [
        legsFilters,
        symbolFilter,
        dateSelection,
        accountIds?.length,
        accountIds?.[0],
        userTimezone,
    ]);
    return { params, getParams };
};

export default useGetFetchLegsParams;
